import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Layers, Move, Ruler, Circle, Triangle, Square,
  MousePointer, Crosshair, Eye, EyeOff, Minus, Paintbrush, Eraser, Trash2, BoxSelect,
  Users, Package, Swords, Sparkles, Cloud,
  Slash, DoorOpen, AppWindow, Shapes
} from "lucide-react";

// ---- Tool catalogue ----
// Flat list kept so the keyboard / state machine in VTTPage doesn't need to
// change. The groups below are purely a UI grouping for the side palette.
const SINGLE_TOOLS = [
  { id: "select",  label: "Select",     icon: MousePointer },
  { id: "move",    label: "Move Token", icon: Move },
  { id: "ping",    label: "Ping",       icon: Crosshair },
];

const MEASURE_TOOLS = [
  { id: "measure_line",   label: "Line",   icon: Minus },
  { id: "measure_cone",   label: "Cone",   icon: Triangle },
  { id: "measure_circle", label: "Circle", icon: Circle },
  { id: "measure_square", label: "Square", icon: Square },
];

const WALL_TOOLS = [
  { id: "wall_add",    label: "Wall (blocks sight + movement)",         icon: Slash },
  { id: "door_add",    label: "Door (toggle open to pass)",             icon: DoorOpen },
  { id: "window_add",  label: "Window (passes light, blocks movement)", icon: AppWindow },
  { id: "wall_erase",  label: "Erase Wall / Door / Window",             icon: Eraser },
];

const FOG_TOOLS = [
  { id: "fog_add",    label: "Fog Brush",     icon: Paintbrush },
  { id: "fog_rect",   label: "Fog Rectangle", icon: BoxSelect },
  { id: "fog_remove", label: "Fog Eraser",    icon: Eraser },
];

const LAYERS = [
  { id: "map",    label: "Map",      icon: Layers },
  { id: "gm",     label: "GM Notes", icon: EyeOff },
  { id: "tokens", label: "Tokens",   icon: Eye },
];

// Reusable expandable tool group. Shows the active sub-tool's icon when any of
// the group's sub-tools is active, otherwise the group's default icon. Clicking
// opens a horizontal popover anchored to the right with the sub-tools.
const ToolGroup = ({ groupId, defaultIcon: DefaultIcon, label, tools, activeTool, setActiveTool }) => {
  const [open, setOpen] = useState(false);
  const activeSub = tools.find(t => t.id === activeTool);
  const Icon = activeSub ? activeSub.icon : DefaultIcon;
  const isActive = !!activeSub;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
            isActive
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          }`}
          title={activeSub ? activeSub.label : label}
          data-testid={`toolgroup-${groupId}`}
        >
          <Icon className="w-4 h-4" />
          {/* Small chevron-style dot indicating this is a group */}
          <span className={`absolute bottom-0.5 right-0.5 w-1 h-1 rounded-full ${isActive ? "bg-primary-foreground/70" : "bg-muted-foreground/50"}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={6}
        className="w-auto p-1 bg-card/95 border-border backdrop-blur-sm"
        data-testid={`toolgroup-${groupId}-popover`}
      >
        <div className="flex items-center gap-0.5">
          {tools.map(t => (
            <button
              key={t.id}
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                activeTool === t.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              }`}
              onClick={() => { setActiveTool(t.id); setOpen(false); }}
              title={t.label}
              data-testid={`tool-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const VTTToolbar = ({
  activeTool,
  setActiveTool,
  activeLayer,
  setActiveLayer,
  gridSize,
  setGridSize,
  measurePersistent,
  setMeasurePersistent,
  isDM = true,
  fogBrushSize = 40,
  setFogBrushSize,
  onClearFog,
  onOpenTokenBrowser,
  onOpenAssetBrowser,
  onOpenSpellBrowser,
  onToggleEncounter,
  encounterOpen = false,
  tokenBrowserOpen = false,
  assetBrowserOpen = false,
  spellBrowserOpen = false,
}) => {
  const stepGrid = (delta) => {
    const next = Math.max(20, Math.min(100, (gridSize || 50) + delta));
    setGridSize(next);
  };

  return (
    <div className="absolute left-3 top-14 z-20 flex flex-col gap-1 w-10" data-testid="vtt-toolbar">
      {/* Primary tool palette (compact, with grouped flyouts) */}
      <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-1 flex flex-col gap-0.5 shadow-lg">
        {SINGLE_TOOLS.map(tool => (
          <button
            key={tool.id}
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
              activeTool === tool.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            }`}
            onClick={() => setActiveTool(tool.id)}
            title={tool.label}
            data-testid={`tool-${tool.id}`}
          >
            <tool.icon className="w-4 h-4" />
          </button>
        ))}

        {/* Measurement shapes — grouped */}
        <ToolGroup
          groupId="measure"
          defaultIcon={Ruler}
          label="Measure (Line / Cone / Circle / Square)"
          tools={MEASURE_TOOLS}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
        />

        {isDM && (
          <>
            {/* Fog of War — grouped */}
            <ToolGroup
              groupId="fog"
              defaultIcon={Cloud}
              label="Fog of War (Brush / Rectangle / Eraser)"
              tools={FOG_TOOLS}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
            />
            {/* Walls / Doors / Windows — grouped */}
            <ToolGroup
              groupId="walls"
              defaultIcon={Shapes}
              label="Walls (Wall / Door / Window / Erase)"
              tools={WALL_TOOLS}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
            />
          </>
        )}
      </div>

      {/* Browsers & combat shortcut group */}
      <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-1 flex flex-col gap-0.5 shadow-lg">
        <button
          className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${tokenBrowserOpen ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}
          onClick={onOpenTokenBrowser}
          title="Token Browser"
          data-testid="open-token-browser"
        ><Users className="w-4 h-4" /></button>
        <button
          className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${assetBrowserOpen ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}
          onClick={onOpenAssetBrowser}
          title="Asset Browser"
          data-testid="open-asset-browser"
        ><Package className="w-4 h-4" /></button>
        {isDM && (
          <button
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${spellBrowserOpen ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}
            onClick={onOpenSpellBrowser}
            title="Spell Browser"
            data-testid="open-spell-browser"
          ><Sparkles className="w-4 h-4" /></button>
        )}
        {isDM && (
          <button
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${encounterOpen ? "bg-red-500 text-white" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}
            onClick={onToggleEncounter}
            title="Encounter Tracker"
            data-testid="open-encounter-tracker"
          ><Swords className="w-4 h-4" /></button>
        )}
      </div>

      {/* Measurement options */}
      {activeTool?.startsWith("measure_") && (
        <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-2 shadow-lg" data-testid="measure-options">
          <button
            className={`w-full text-[10px] px-1 py-1 rounded ${measurePersistent ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
            onClick={() => setMeasurePersistent(!measurePersistent)}
            title={measurePersistent ? "Shapes stay on map" : "Shapes disappear after 3s"}
          >
            {measurePersistent ? "Persist" : "Temp"}
          </button>
        </div>
      )}

      {/* Fog brush options (DM only) */}
      {isDM && (activeTool === "fog_add" || activeTool === "fog_remove" || activeTool === "fog_rect") && (
        <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-2 shadow-lg space-y-2" data-testid="fog-brush-options">
          {(activeTool === "fog_add" || activeTool === "fog_remove") && (
            <>
              <div className="text-[8px] text-muted-foreground text-center uppercase tracking-wider">
                {activeTool === "fog_add" ? "Brush" : "Eraser"} Size
              </div>
              <div className="text-[10px] text-center text-foreground font-medium">{fogBrushSize}px</div>
              <input
                type="range"
                min={10}
                max={120}
                step={5}
                value={fogBrushSize}
                onChange={(e) => setFogBrushSize?.(parseInt(e.target.value))}
                className="w-full h-1 accent-primary"
                data-testid="fog-brush-slider"
              />
              <div className="flex justify-center">
                <div className="rounded-full border border-muted-foreground/30" style={{ width: Math.min(32, fogBrushSize / 2), height: Math.min(32, fogBrushSize / 2), backgroundColor: activeTool === "fog_add" ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.3)" }} />
              </div>
            </>
          )}
          {onClearFog && (
            <button
              className="w-full text-[9px] px-1 py-1 rounded text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-1"
              onClick={onClearFog}
              data-testid="clear-fog-btn"
            >
              <Trash2 className="w-3 h-3" /> Clear All Fog
            </button>
          )}
        </div>
      )}

      {/* Layer controls (DM only) */}
      {isDM && (
        <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-1 flex flex-col gap-0.5 shadow-lg" data-testid="layer-controls">
          <div className="text-[8px] text-muted-foreground text-center uppercase tracking-wider py-0.5">Layers</div>
          {LAYERS.map(layer => (
            <button
              key={layer.id}
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                activeLayer === layer.id
                  ? "bg-primary/30 text-primary"
                  : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              }`}
              onClick={() => setActiveLayer(layer.id)}
              title={layer.label}
              data-testid={`layer-${layer.id}`}
            >
              <layer.icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      )}

      {/* Grid size control (DM only) — +/- buttons */}
      {isDM && (
        <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg p-2 shadow-lg" data-testid="grid-size-control">
          <div className="text-[8px] text-muted-foreground text-center uppercase tracking-wider mb-1">Grid</div>
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => stepGrid(-5)}
              className="h-6 w-6 rounded-sm bg-muted/30 hover:bg-muted/60 text-foreground text-sm leading-none border border-border flex items-center justify-center"
              data-testid="grid-size-down"
              title="Smaller grid (-5px)"
              aria-label="Smaller grid"
            >−</button>
            <div className="text-[11px] text-center text-foreground font-medium font-mono w-10" data-testid="grid-size-value">{gridSize}px</div>
            <button
              onClick={() => stepGrid(5)}
              className="h-6 w-6 rounded-sm bg-muted/30 hover:bg-muted/60 text-foreground text-sm leading-none border border-border flex items-center justify-center"
              data-testid="grid-size-up"
              title="Larger grid (+5px)"
              aria-label="Larger grid"
            >+</button>
          </div>
        </div>
      )}
    </div>
  );
};
